import { useState } from 'react';
import type { JSX } from 'react';
import type { JournalEntryOwner } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';

export interface OwnerTarget {
  readonly key: string;
  readonly label: string;
  readonly group: 'beat' | 'objective';
  readonly owner: JournalEntryOwner;
}

export interface EntryOwnerBarProps {
  /** «в бите «…»» — set when the entry lives in a beat / objective / quest; undefined — it is shared. */
  readonly caption: string | undefined;
  /** Where a shared entry can be moved to: the beats and the objectives of quests. */
  readonly targets: readonly OwnerTarget[];
  readonly t: (key: keyof TranslationKeys) => string;
  /** «В общий список». */
  readonly onShare: () => void;
  /** «Убрать в бит / цель» with the chosen place. */
  readonly onMove: (owner: JournalEntryOwner) => void;
}

/**
 * P03: where a note lives (Studio's entry view). A nested note shows its
 * owner and «To the shared list»; a shared one offers «Move into a beat /
 * objective» with the choice. Both are commands answered by the server; the
 * bar shows what the server confirmed.
 */
export function EntryOwnerBar({ caption, targets, t, onShare, onMove }: EntryOwnerBarProps): JSX.Element | null {
  const [picking, setPicking] = useState<boolean>(false);
  if (caption !== undefined) {
    return (
      <div className="entry-owner-bar flex items-center gap-2 border-b border-line/60 px-3 py-1.5 text-meta" data-testid="entry-owner-bar">
        <span className="min-w-0 flex-1 truncate text-muted-foreground" data-testid="entry-owner-caption">{caption}</span>
        <button
          type="button"
          className="flex-none rounded px-1.5 py-0.5 text-hl hover:bg-hl/10"
          data-testid="entry-owner-share"
          onClick={onShare}
        >
          {t('studio.owner.toShared')}
        </button>
      </div>
    );
  }
  if (targets.length === 0) return null;
  const beatTargets = targets.filter((x) => x.group === 'beat');
  const objectiveTargets = targets.filter((x) => x.group === 'objective');
  return (
    <div className="entry-owner-bar flex items-center gap-2 border-b border-line/60 px-3 py-1.5 text-meta" data-testid="entry-owner-bar">
      {picking ? (
        <select
          autoFocus
          className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 text-meta text-fg focus:border-hl/50 focus:outline-none"
          data-testid="entry-owner-pick"
          aria-label={t('studio.owner.movePick')}
          defaultValue=""
          onChange={(e): void => {
            const target = targets.find((x) => x.key === e.target.value);
            setPicking(false);
            if (target !== undefined) onMove(target.owner);
          }}
          onBlur={(): void => setPicking(false)}
        >
          <option value="" disabled>{t('studio.owner.movePick')}</option>
          {beatTargets.length > 0 ? (
            <optgroup label={t('studio.owner.moveBeatGroup')}>
              {beatTargets.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </optgroup>
          ) : null}
          {objectiveTargets.length > 0 ? (
            <optgroup label={t('studio.owner.moveObjectiveGroup')}>
              {objectiveTargets.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </optgroup>
          ) : null}
        </select>
      ) : (
        <button
          type="button"
          className="ml-auto flex-none rounded px-1.5 py-0.5 text-muted-foreground hover:bg-panel-2 hover:text-fg"
          data-testid="entry-owner-move"
          onClick={(): void => setPicking(true)}
        >
          {t('studio.owner.move')}
        </button>
      )}
    </div>
  );
}
